import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import { commissionLabel, discountLabel } from '../utils/benefit';
import BusinessEditModal from './BusinessEditModal';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-');
const fmtMoney = (n) => `₪${Number(n || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;
const fullUrl = (url) => (!url ? '' : /^https?:/i.test(url) ? url : `${utils.BaseUrl}${url.startsWith('/') ? '' : '/'}${url}`);

const ROLE_LABEL = { owner: 'בעלים', manager: 'מנהל', employee: 'עובד' };
const STATUS_META = {
  active:    { label: 'פעיל', color: '#1f6feb', bg: '#eef4ff' },
  expired:   { label: 'פג תוקף', color: '#697386', bg: '#f1f3f5' },
  redeemed:  { label: 'מומש', color: '#2e7d32', bg: '#e8f5e9' },
  cancelled: { label: 'בוטל', color: '#d93838', bg: '#fff1f1' },
};
const BUSINESS_TYPE_LABEL = { physical: '🏪 חנות פיזית', online: '🌐 חנות מקוונת', both: '🏪+🌐 פיזית ומקוונת' };
const PLATFORM_LABEL = { woocommerce: 'WooCommerce', shopify: 'Shopify' };

// מה חסר כדי שחנות מקוונת תעבוד אוטומטית (קוד באתר + מימוש אוטומטי) - מוצג
// בדף בית העסק כדי שיהיה ברור מה עוד צריך לקבל מבעל העסק
function onlineChecklist(b) {
  const connected = b.onlinePlatform === 'shopify' ? !!b.shopifyConnected : !!b.wooConnected;
  const items = [
    { ok: !!b.onlinePlatform, label: b.onlinePlatform ? `פלטפורמה: ${PLATFORM_LABEL[b.onlinePlatform]}` : 'לא נבחרה פלטפורמה (עריכת בית העסק)' },
    { ok: !!b.onlineStoreUrl, label: b.onlineStoreUrl ? 'כתובת אתר הוזנה' : 'חסרה כתובת אתר (בעריכה כאן, או ע"י בעל העסק באפליקציה)' },
  ];
  if (b.onlinePlatform === 'shopify') {
    items.push({
      ok: connected,
      label: connected
        ? `אפליקציית איגוד מותקנת בחנות ${b.shopifyShop}${b.shopifyLastSeenAt ? ` · פעילות אחרונה ${fmtDateTime(b.shopifyLastSeenAt)}` : ''}`
        : 'בעל העסק עוד לא התקין את אפליקציית איגוד בשופיפיי (ניהול החנות באפליקציה ← "חיבור החנות לאיגוד")',
    });
  } else if (b.onlinePlatform === 'woocommerce') {
    items.push({ ok: !!b.wooConnected, label: b.wooConnected ? 'נוצר מפתח חיבור' : 'בעל העסק עוד לא יצר מפתח חיבור (ניהול החנות באפליקציה)' });
    items.push({
      ok: !!b.wooLastSeenAt,
      label: b.wooLastSeenAt ? `התוסף באתר מחובר · התחבר לאחרונה ${fmtDateTime(b.wooLastSeenAt)}` : 'התוסף עדיין לא התחבר מהאתר (התקנה + הדבקת המפתח בוורדפרס)',
    });
  }
  return { items, connected };
}

// טווח תאריכים לסטטיסטיקה ולטבלת הקופונים. ברירת מחדל: חודש (30 הימים האחרונים)
const DAY_MS = 24 * 60 * 60 * 1000;
const RANGE_OPTIONS = [
  { key: '24h', label: '24 שעות', ms: DAY_MS },
  { key: 'week', label: 'שבוע', ms: 7 * DAY_MS },
  { key: 'month', label: 'חודש', ms: 30 * DAY_MS },
  { key: '3months', label: '3 חודשים', ms: 90 * DAY_MS },
  { key: 'custom', label: 'אחר' },
];
// ms -> 'YYYY-MM-DDTHH:mm:ss' בשעון מקומי (כמו getdate() בשרת), לטווח החשיפות
const toLocalIso = (ms) => {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};
const SOURCE_LABEL = {
  feed: 'עמוד ראשי', deals: 'שורת מבצעים', new: 'הצטרפו לאחרונה', recommended: 'המומלצים', map: 'מפה',
  push: 'התראה', redeemed: 'אחרי מימוש', share: 'שיתוף', other: 'קישור ישיר / אחר',
};
const pct = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '-');

const toInputDate = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const STATUS_FILTERS = [
  { key: '', label: 'הכל' },
  { key: 'redeemed', label: 'מומשו' },
  { key: 'active', label: 'פעילים' },
  { key: 'expired', label: 'פגי תוקף' },
  { key: 'cancelled', label: 'בוטלו' },
];

// דף בית עסק בבק אופיס (/businesses/:id) - נפתח מלחיצה על שם בית עסק בטבלת
// המשתמשים או בטאב "בתי עסק". פרטים, בעלים ועובדים (כולל מי שעוד לא התחבר),
// סטטיסטיקה, וכל הקופונים שהונפקו - פעילים, פגי תוקף, מומשו ובוטלו
// (spBOGetBusinessDetails). מחזור/עמלה נספרים רק על עסקאות שמומשו ולא בוטלו
const BusinessDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rangeKey, setRangeKey] = useState('month');
  const [customFrom, setCustomFrom] = useState(() => toInputDate(Date.now() - 30 * DAY_MS));
  const [customTo, setCustomTo] = useState(() => toInputDate(Date.now()));
  // חיבור חנות מטעם הצוות (ראו StoreConnectActions)
  const [connectShop, setConnectShop] = useState('');
  const [connectLink, setConnectLink] = useState(null);
  const [wooKey, setWooKey] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectError, setConnectError] = useState('');
  // עריכת פרטי העסק (שם, טלפון, אימייל וכו') ישירות מהדף - אותו מודל כמו בטאב בתי עסק.
  // reloadKey - טעינה מחדש של הדף אחרי שמירה
  const [editing, setEditing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const resp = await BOAPI.fetchBusinessDetails(Number(id));
        if (resp?.[0]?.[0]?.code === 0) {
          setData({
            business: resp[1]?.[0] || null,
            staff: resp[2] || [],
            stats: resp[3]?.[0] || {},
            transactions: resp[4] || [],
            discountItems: resp[5] || [],
            featuredProducts: resp[6] || [],
          });
        } else {
          setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת בית העסק');
        }
      } catch {
        setError('שגיאה בתקשורת עם השרת');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, reloadKey]);

  // גבולות הטווח הנבחר (ms). "אחר" - מתחילת יום ה"מ-" ועד סוף יום ה"עד"
  const [rangeFrom, rangeTo] = useMemo(() => {
    if (rangeKey === 'custom') {
      const from = customFrom ? new Date(`${customFrom}T00:00:00`).getTime() : 0;
      const to = customTo ? new Date(`${customTo}T23:59:59.999`).getTime() : Date.now();
      return [from, to];
    }
    const opt = RANGE_OPTIONS.find((r) => r.key === rangeKey);
    return [Date.now() - opt.ms, Date.now()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, customFrom, customTo, data]);
  const inRange = (d) => {
    if (!d) return false;
    const t = new Date(d).getTime();
    return t >= rangeFrom && t <= rangeTo;
  };

  // סטטיסטיקה לטווח: ספירות לפי תאריך ההנפקה, סכומים (מחזור/הנחות/עמלה) לפי
  // תאריך המימוש - כך עמלה של קוד שהונפק בסוף חודש ומומש בתחילת הבא נספרת בחודש
  // שבו מומש בפועל. "פעילים כרגע" - לא תלוי טווח (מה שפעיל עכשיו)
  const rangeStats = useMemo(() => {
    const all = data?.transactions || [];
    const issued = all.filter((t) => inRange(t.issuedAt));
    const redeemed = all.filter((t) => t.status === 'redeemed' && inRange(t.redeemedAt));
    const sum = (list, f) => list.reduce((acc, t) => acc + Number(t[f] || 0), 0);
    return {
      issuedCount: issued.length,
      redeemedCount: redeemed.length,
      expiredCount: issued.filter((t) => t.displayStatus === 'expired').length,
      cancelledCount: issued.filter((t) => t.displayStatus === 'cancelled').length,
      activeNow: all.filter((t) => t.displayStatus === 'active').length,
      salesAmount: sum(redeemed, 'amount'),
      discountAmount: sum(redeemed, 'discountAmount'),
      commissionAmount: sum(redeemed, 'commissionAmount'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, rangeFrom, rangeTo]);

  // חשיפות - נטען מהשרת לכל שינוי טווח (spBOGetBusinessViewStats)
  const [views, setViews] = useState(null);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [showVisitors, setShowVisitors] = useState(false);
  useEffect(() => {
    if (!id || !rangeFrom) return;
    let cancelled = false;
    setViewsLoading(true);
    BOAPI.fetchBusinessViewStats(Number(id), toLocalIso(rangeFrom), toLocalIso(rangeTo))
      .then((resp) => {
        if (cancelled) return;
        if (resp?.[0]?.[0]?.code === 0) {
          setViews({ totals: resp[1]?.[0] || {}, bySource: resp[2] || [], byDay: resp[3] || [], topVisitors: resp[4] || [] });
        } else {
          setViews(null);
        }
      })
      .catch(() => !cancelled && setViews(null))
      .finally(() => !cancelled && setViewsLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, rangeKey, customFrom, customTo]);

  const visibleTransactions = useMemo(
    () => (data?.transactions || []).filter((t) => inRange(t.issuedAt) && (!statusFilter || t.displayStatus === statusFilter)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, statusFilter, rangeFrom, rangeTo]
  );

  if (loading) return <div style={s.page}><div style={s.center}>טוען...</div></div>;
  if (error || !data?.business) return <div style={s.page}><div style={s.errorBanner}>❌ {error || 'בית העסק לא נמצא'}</div></div>;

  const { business: b, staff, stats, discountItems, featuredProducts } = data;
  const isOnline = b.businessType && b.businessType !== 'physical';
  const checklist = isOnline ? onlineChecklist(b) : null;
  const categories = b.categoryLabels?.length ? b.categoryLabels : [b.categoryLabel];

  return (
    <div style={s.page}>
      <button style={s.backBtn} onClick={() => navigate(-1)}>→ חזרה</button>

      {/* ── כותרת ── */}
      <div style={s.card}>
        <div style={s.header}>
          {b.logoUrl ? <img src={fullUrl(b.logoUrl)} alt="" style={s.logo} /> : <div style={s.logoPlaceholder}>{(b.name || '?')[0]}</div>}
          <div style={{ flex: 1 }}>
            <div style={s.titleRow}>
              <h1 style={s.title}>{b.name}</h1>
              <button style={s.editBtn} onClick={() => setEditing(true)}>✏️ עריכת פרטים</button>
            </div>
            <div style={s.chips}>
              {categories.map((c) => <span key={c} style={s.chip}>{c}</span>)}
              <span style={{ ...s.chip, ...(b.active ? s.chipGreen : s.chipGray) }}>{b.active ? 'פעיל' : 'מושבת'}</span>
              <span style={{ ...s.chip, ...(b.ownerConfirmedAt ? s.chipGreen : s.chipOrange) }}>{b.ownerConfirmedAt ? 'אושר ע"י הבעלים' : 'ממתין לאישור בעלים'}</span>
              <span style={s.chip}>{BUSINESS_TYPE_LABEL[b.businessType] || BUSINESS_TYPE_LABEL.physical}</span>
              {checklist?.connected && <span style={{ ...s.chip, ...s.chipBlue }}>🛒 מחובר ל-{PLATFORM_LABEL[b.onlinePlatform]}</span>}
            </div>
          </div>
        </div>
        <div style={s.infoGrid}>
          <Info label={b.discountFixed != null ? 'הטבה (סכום קבוע)' : 'הנחה כללית'} value={discountLabel(b)} />
          <Info label="עמלת סליקה" value={commissionLabel(b)} />
          {b.businessType !== 'online' && <Info label="כתובת" value={[b.address, b.city].filter(Boolean).join(', ') || '-'} />}
          <Info label="טלפון העסק" value={b.phone || '-'} />
          <Info label="אימייל העסק" value={b.email ? <a href={`mailto:${b.email}`}>{b.email}</a> : '-'} />
          <Info label="הצטרף" value={fmtDateTime(b.iTime)} />
          {b.onlineStoreUrl && <Info label="אתר" value={<a href={b.onlineStoreUrl} target="_blank" rel="noreferrer">{b.onlineStoreUrl}</a>} />}
        </div>
        {b.benefitDescription && <p style={s.benefit}>פירוט ההטבה: {b.benefitDescription}</p>}

        {/* חנות מקוונת - מה כבר חובר ומה עוד חסר מבעל העסק */}
        {checklist && (
          <div style={s.wooBox}>
            <div style={{ flex: 1 }}>
              <strong>חיבור החנות המקוונת {checklist.connected ? '✅' : '⏳'}</strong>
              <div style={s.sub}>
                {checklist.connected
                  ? 'קודי איגוד עובדים בעמוד התשלום באתר, והמימושים נרשמים אוטומטית.'
                  : 'עד שהחיבור יושלם, הלקוחות מקבלים קוד רגיל ובעל העסק מממש אותו באפליקציה.'}
              </div>
              <ul style={s.checklist}>
                {checklist.items.map((it) => (
                  <li key={it.label} style={{ ...s.checkItem, color: it.ok ? '#2e7d32' : '#b26a00' }}>{it.ok ? '✓' : '○'} {it.label}</li>
                ))}
                <li style={{ ...s.checkItem, color: '#697386' }}>
                  {featuredProducts.length > 0 ? `✓ ${featuredProducts.length} מוצרים מוצגים בעמוד בית העסק` : '○ אין מוצרים מוצגים (אופציונלי - בעל העסק בוחר באפליקציה)'}
                </li>
              </ul>

              {/* לבעלי עסק שלא מחברים לבד - הצוות מכין קישור/מפתח ושולח להם */}
              {!checklist.connected && b.onlinePlatform === 'shopify' && (
                <div style={s.connectBox}>
                  <strong style={{ fontSize: 13 }}>חיבור מטעם איגוד</strong>
                  <div style={s.sub}>
                    יוצרים קישור התקנה (בתוקף 7 ימים) ושולחים לבעל העסק. הוא לוחץ, מתחבר לשופיפיי ומאשר "Install" - וזהו.
                    כתובת החנות לא חובה: אם תושאר ריקה, הקישור ישאל אותו.
                  </div>
                  <div style={s.connectRow}>
                    <input
                      style={s.connectInput}
                      dir="ltr"
                      placeholder="my-shop.myshopify.com (אופציונלי)"
                      value={connectShop}
                      onChange={(e) => setConnectShop(e.target.value)}
                    />
                    <button
                      style={s.connectBtn}
                      disabled={connectBusy}
                      onClick={async () => {
                        setConnectBusy(true);
                        setConnectError('');
                        try {
                          const resp = await BOAPI.shopifyInstallLink(b.id, connectShop.trim());
                          if (resp?.[0]?.[0]?.code === 0) setConnectLink(resp[1]?.[0]?.url || null);
                          else setConnectError(resp?.[0]?.[0]?.msg || 'יצירת הקישור נכשלה');
                        } catch {
                          setConnectError('שגיאה בתקשורת עם השרת');
                        } finally {
                          setConnectBusy(false);
                        }
                      }}
                    >
                      {connectBusy ? '...' : 'יצירת קישור התקנה'}
                    </button>
                  </div>
                  {connectLink && (
                    <div style={s.connectResult}>
                      <div style={s.connectValue} dir="ltr">{connectLink}</div>
                      <div style={s.connectRow}>
                        <button style={s.connectGhostBtn} onClick={() => navigator.clipboard?.writeText(connectLink)}>📋 העתקה</button>
                        <button
                          style={s.connectGhostBtn}
                          onClick={() => {
                            const text = `שלום, זה הקישור לחיבור חנות השופיפיי של "${b.name}" למועדון איגוד. לחצו, התחברו לחנות ואשרו "Install": ${connectLink}`;
                            const digits = String(b.ownerPhone || b.phone || '').replace(/[^0-9]/g, '');
                            const intl = digits.startsWith('972') ? digits : digits.startsWith('0') ? `972${digits.slice(1)}` : digits;
                            window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
                          }}
                        >
                          💬 שליחה בוואטסאפ
                        </button>
                      </div>
                      <div style={s.sub}>אחרי שבעל העסק יאשר, רעננו את הדף - הסטטוס יתעדכן ל"מחובר".</div>
                    </div>
                  )}
                  {connectError && <div style={s.connectError}>{connectError}</div>}
                </div>
              )}

              {b.onlinePlatform === 'woocommerce' && (
                <div style={s.connectBox}>
                  <strong style={{ fontSize: 13 }}>מפתח חיבור מטעם איגוד</strong>
                  <div style={s.sub}>
                    ליצירת מפתח כאן - אם אתם מתקינים את התוסף בעצמכם (עם גישה לוורדפרס של העסק), או שולחים את המפתח לבעל העסק.
                    {b.wooConnected && ' שימו לב: כבר קיים מפתח - מפתח חדש מבטל אותו מיד, וצריך להדביק את החדש בתוסף.'}
                  </div>
                  {wooKey ? (
                    <div style={s.connectResult}>
                      <div style={s.connectValue} dir="ltr">{wooKey}</div>
                      <div style={s.connectRow}>
                        <button style={s.connectGhostBtn} onClick={() => navigator.clipboard?.writeText(wooKey)}>📋 העתקת המפתח</button>
                        <a style={s.connectGhostBtn} href={`${utils.BaseUrl}/downloads/egud-club-woocommerce.zip`} target="_blank" rel="noreferrer">⬇️ הורדת התוסף</a>
                      </div>
                      <div style={{ ...s.sub, color: '#d93838' }}>המפתח מוצג פעם אחת בלבד - העתיקו אותו עכשיו.</div>
                    </div>
                  ) : (
                    <button
                      style={{ ...s.connectBtn, marginTop: 8 }}
                      disabled={connectBusy}
                      onClick={async () => {
                        if (b.wooConnected && !window.confirm('ליצור מפתח חדש? המפתח הקיים יפסיק לעבוד מיד.')) return;
                        setConnectBusy(true);
                        setConnectError('');
                        try {
                          const resp = await BOAPI.createWooKey(b.id);
                          if (resp?.[0]?.[0]?.code === 0) {
                            setWooKey(resp[1]?.[0]?.key || null);
                            setData((prev) => ({ ...prev, business: { ...prev.business, wooConnected: true, wooLastSeenAt: null } }));
                          } else {
                            setConnectError(resp?.[0]?.[0]?.msg || 'יצירת המפתח נכשלה');
                          }
                        } catch {
                          setConnectError('שגיאה בתקשורת עם השרת');
                        } finally {
                          setConnectBusy(false);
                        }
                      }}
                    >
                      {connectBusy ? '...' : b.wooConnected ? 'יצירת מפתח חדש' : 'יצירת מפתח חיבור'}
                    </button>
                  )}
                  {connectError && <div style={s.connectError}>{connectError}</div>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── טווח תאריכים - חל על הסטטיסטיקה ועל טבלת הקופונים ── */}
      <div style={s.rangeBar}>
        <span style={s.rangeLabel}>טווח:</span>
        <div style={s.segment} role="group" aria-label="טווח תאריכים">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              aria-pressed={rangeKey === r.key}
              style={{ ...s.segmentBtn, ...(rangeKey === r.key ? s.segmentBtnActive : {}) }}
            >
              {r.label}
            </button>
          ))}
        </div>
        {rangeKey === 'custom' && (
          <div style={s.customRange}>
            <label style={s.dateLabel}>
              מ-
              <input type="date" style={s.dateInput} value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} />
            </label>
            <label style={s.dateLabel}>
              עד
              <input type="date" style={s.dateInput} value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} />
            </label>
          </div>
        )}
        <span style={s.rangeHint}>{new Date(rangeFrom).toLocaleDateString('he-IL')} - {new Date(rangeTo).toLocaleDateString('he-IL')}</span>
      </div>

      {/* ── סטטיסטיקה (לטווח הנבחר) ── */}
      <style>{`@media (max-width: 700px) { .bd-stats { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }`}</style>
      <div style={s.stats} className="bd-stats">
        <Stat label="קופונים שהונפקו" value={rangeStats.issuedCount} color="#1a1f36" />
        <Stat label="מומשו" value={rangeStats.redeemedCount} color="#2e7d32" />
        <Stat label="פעילים כרגע" value={rangeStats.activeNow} color="#1f6feb" />
        <Stat label="פגו בלי מימוש" value={rangeStats.expiredCount} color="#697386" />
        <Stat label="בוטלו" value={rangeStats.cancelledCount} color="#d93838" />
        <Stat label="מחזור" value={fmtMoney(rangeStats.salesAmount)} color="#1f6feb" />
        <Stat label="הנחות ללקוחות" value={fmtMoney(rangeStats.discountAmount)} color="#a06d00" />
        <Stat label="עמלה" value={fmtMoney(rangeStats.commissionAmount)} color="#8B5CF6" />
      </div>
      <div style={s.totalsLine}>
        סה״כ מאז ההצטרפות: {stats.issuedCount || 0} קופונים · {stats.redeemedCount || 0} מומשו · מחזור {fmtMoney(stats.salesAmount)} · עמלה {fmtMoney(stats.commissionAmount)}
      </div>

      {/* ── חשיפות: משפך צפייה -> קופון -> מימוש (לטווח הנבחר) ── */}
      <div style={s.card}>
        <div style={s.tableHeader}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>חשיפות</h3>
          {viewsLoading && <span style={s.muted}>טוען...</span>}
        </div>
        {!views ? (
          <div style={s.muted}>{viewsLoading ? '' : 'אין עדיין נתוני חשיפות (נאספים מהגרסה החדשה של האפליקציה)'}</div>
        ) : (
          <>
            <div style={s.funnel} className="bd-stats">
              <Stat label="צפיות בעמוד" value={views.totals.views || 0} color="#1a1f36" />
              <Stat label="מבקרים ייחודיים" value={views.totals.uniqueVisitors || 0} color="#1f6feb" />
              <Stat
                label={`הנפיקו קופון · ${pct(views.totals.visitorsWhoIssued || 0, views.totals.loggedInVisitors || 0)}`}
                value={views.totals.visitorsWhoIssued || 0}
                color="#a06d00"
              />
              <Stat
                label={`מימשו · ${pct(views.totals.visitorsWhoRedeemed || 0, views.totals.visitorsWhoIssued || 0)} מהמנפיקים`}
                value={views.totals.visitorsWhoRedeemed || 0}
                color="#2e7d32"
              />
            </div>
            <div style={s.totalsLine}>
              {views.totals.loggedInVisitors || 0} מחוברים · {views.totals.guestVisitors || 0} אורחים · {views.totals.repeatVisitors || 0} חזרו יותר מפעם אחת
              {' · '}אחוז ההנפקה מחושב מתוך המבקרים המחוברים (אורח לא יכול להנפיק קופון)
            </div>

            {views.bySource.length > 0 && (
              <div style={s.sourceRow}>
                <span style={s.rangeLabel}>מאיפה הגיעו:</span>
                {views.bySource.map((r) => (
                  <span key={r.source} style={s.chip}>
                    {SOURCE_LABEL[r.source] || (r.source?.startsWith('sec_') ? 'קרוסלה בעמוד הראשי' : r.source)} · {r.views} ({pct(r.views, views.totals.views || 0)})
                  </span>
                ))}
              </div>
            )}

            {views.topVisitors.length > 0 && (
              <>
                <button style={s.linkBtn} onClick={() => setShowVisitors((v) => !v)} aria-expanded={showVisitors}>
                  {showVisitors ? '▲ הסתרת המבקרים התכופים' : `▼ המבקרים התכופים (${views.topVisitors.length})`}
                </button>
                {showVisitors && (
                  <div style={s.tableWrap}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>משתמש</th>
                          <th style={s.th}>ביקורים</th>
                          <th style={s.th}>ביקור אחרון</th>
                          <th style={s.th}>הנפיק קופון</th>
                          <th style={s.th}>מימש</th>
                        </tr>
                      </thead>
                      <tbody>
                        {views.topVisitors.map((u) => (
                          <tr key={u.userId}>
                            <td style={s.td}>
                              <button style={s.linkBtn} onClick={() => navigate(`/users/${u.userId}`)}>{u.name || 'ללא שם'}</button>
                              <div style={s.sub}>{u.phone}</div>
                            </td>
                            <td style={s.td}><strong>{u.visits}</strong></td>
                            <td style={s.td}>{fmtDateTime(u.lastVisitAt)}</td>
                            <td style={s.td}>{u.issued ? '✓' : '-'}</td>
                            <td style={s.td}>{u.redeemed ? '✓' : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div style={s.twoCols} className="bd-two-cols">
        <style>{`@media (max-width: 900px) { .bd-two-cols { grid-template-columns: 1fr !important; } }`}</style>

        {/* ── בעלים ועובדים ── */}
        <div style={s.card}>
          <h3 style={s.sectionTitle}>בעלים ועובדים ({staff.length})</h3>
          {staff.length === 0 ? (
            <div style={s.muted}>אין עדיין בעלים משויך (ממתין לאישור קישור)</div>
          ) : (
            staff.map((m) => (
              <div key={`${m.userId}-${m.role}`} style={s.staffRow}>
                <div>
                  <button style={s.linkBtn} onClick={() => navigate(`/users/${m.userId}`)}>{m.name || 'ללא שם'}</button>
                  <div style={s.sub}>{m.phone}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {m.pendingInvite && <span style={{ ...s.chip, ...s.chipOrange }}>הוזמן - טרם התחבר</span>}
                  <span style={{ ...s.chip, ...(m.role === 'owner' ? s.chipBlue : {}) }}>{ROLE_LABEL[m.role] || m.role}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── הנחות לפי מוצר ── */}
        <div style={s.card}>
          <h3 style={s.sectionTitle}>הנחות לפי מוצר</h3>
          {b.discountFixed != null ? (
            <div style={s.muted}>ההטבה בסכום קבוע ({discountLabel(b)} לכל רכישה) - טבלת האחוזים לפי מוצר לא חלה</div>
          ) : discountItems.length === 0 ? (
            <div style={s.muted}>הנחה אחידה של {b.discountPercent}% לכל המוצרים</div>
          ) : (
            <>
              {discountItems.map((d, i) => (
                <div key={i} style={s.staffRow}>
                  <span>{d.label}</span>
                  <strong>{d.discountPercent > 0 ? `${d.discountPercent}%` : 'ללא הנחה'}</strong>
                </div>
              ))}
              <div style={s.staffRow}>
                <span style={s.muted}>שאר המוצרים</span>
                <strong>{b.discountPercent}%</strong>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── קופונים ועסקאות ── */}
      <div style={s.card}>
        <div style={s.tableHeader}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>קופונים ועסקאות</h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)} style={{ ...s.filterBtn, ...(statusFilter === f.key ? s.filterBtnActive : {}) }}>{f.label}</button>
            ))}
          </div>
        </div>

        {visibleTransactions.length === 0 ? (
          <div style={s.muted}>אין קופונים להצגה</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>הונפק</th>
                  <th style={s.th}>לקוח</th>
                  <th style={s.th}>קוד</th>
                  <th style={s.th}>סטטוס</th>
                  <th style={s.th}>מומש</th>
                  <th style={s.th}>ערוץ</th>
                  <th style={s.th}>סכום</th>
                  <th style={s.th}>הנחה</th>
                  <th style={s.th}>עמלה</th>
                </tr>
              </thead>
              <tbody>
                {visibleTransactions.map((t) => {
                  const meta = STATUS_META[t.displayStatus] || STATUS_META.active;
                  const cancelled = t.status === 'cancelled';
                  return (
                    <tr key={t.id}>
                      <td style={s.td}>{fmtDateTime(t.issuedAt)}</td>
                      <td style={s.td}>{t.customerName}<div style={s.sub}>{t.customerPhone}</div></td>
                      <td style={{ ...s.td, fontFamily: 'monospace' }}>{t.code}</td>
                      <td style={s.td}>
                        <span style={{ ...s.chip, background: meta.bg, color: meta.color }}>{meta.label}</span>
                        {cancelled && <div style={s.sub}>{fmtDateTime(t.cancelledAt)}{t.cancelReason ? ` · ${t.cancelReason}` : ''}</div>}
                        {t.displayStatus === 'active' && <div style={s.sub}>בתוקף עד {fmtDateTime(t.expiresAt)}</div>}
                      </td>
                      <td style={s.td}>
                        {t.redeemedAt ? fmtDateTime(t.redeemedAt) : '-'}
                        {t.redeemedByName && <div style={s.sub}>ע"י {t.redeemedByName}</div>}
                      </td>
                      <td style={s.td}>{t.channel === 'online' ? <>🛒 אתר{t.wooOrderId ? <div style={s.sub}>#{t.wooOrderId}</div> : null}</> : t.redeemedAt ? '🏪 חנות' : '-'}</td>
                      <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{t.amount != null ? fmtMoney(t.amount) : t.declaredAmount != null ? <span style={s.sub}>משוער {fmtMoney(t.declaredAmount)}</span> : '-'}</td>
                      <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{t.discountAmount != null ? fmtMoney(t.discountAmount) : discountLabel(t)}</td>
                      <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{t.commissionAmount != null ? fmtMoney(t.commissionAmount) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <BusinessEditModal
        business={editing ? b : null}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); setReloadKey((k) => k + 1); }}
      />
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div style={s.infoLabel}>{label}</div>
    <div style={s.infoValue}>{value}</div>
  </div>
);

const Stat = ({ label, value, color }) => (
  <div style={s.stat}>
    <div style={{ ...s.statValue, color }}>{value}</div>
    <div style={s.statLabel}>{label}</div>
  </div>
);

const s = {
  page:         { direction: 'rtl', fontFamily: 'system-ui, -apple-system, sans-serif', background: '#f6f8fb', minHeight: '100vh', padding: '1.5rem', maxWidth: 1300, margin: '0 auto' },
  backBtn:      { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, marginBottom: 14 },
  center:       { padding: 60, textAlign: 'center', color: '#999' },
  errorBanner:  { background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6 },
  card:         { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 16, padding: 20, marginBottom: 16 },
  header:       { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 },
  logo:         { width: 72, height: 72, objectFit: 'contain', borderRadius: 12, border: '1px solid #e3e8ee', background: '#fff' },
  logoPlaceholder: { width: 72, height: 72, borderRadius: 12, background: '#fdf1e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#a6552a' },
  title:        { margin: '0 0 8px', fontSize: 24, color: '#1a1f36' },
  titleRow:     { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' },
  editBtn:      { padding: '8px 14px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, marginBottom: 8 },
  chips:        { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip:         { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, background: '#f1f3f5', color: '#4f566b' },
  chipGreen:    { background: '#e8f5e9', color: '#2e7d32' },
  chipGray:     { background: '#f1f3f5', color: '#697386' },
  chipOrange:   { background: '#fff8e1', color: '#a06d00' },
  chipBlue:     { background: '#eef4ff', color: '#1f6feb' },
  infoGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 },
  infoLabel:    { fontSize: 12, color: '#8792a2', marginBottom: 2 },
  infoValue:    { fontSize: 14, color: '#1a1f36', fontWeight: 600, wordBreak: 'break-word' },
  benefit:      { margin: '14px 0 0', color: '#4f566b', fontSize: 13.5 },
  connectBox:   { marginTop: 14, padding: 12, background: '#fff', border: '1px solid #e3e8ee', borderRadius: 10 },
  connectRow:   { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 },
  connectInput: { flex: 1, minWidth: 220, padding: '8px 10px', border: '1px solid #d0d7de', borderRadius: 8, fontSize: 13 },
  connectBtn:   { padding: '8px 16px', borderRadius: 8, border: 0, background: '#1f6feb', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  connectGhostBtn: { padding: '7px 12px', borderRadius: 8, border: '1px solid #d0d7de', background: '#fff', color: '#1a1f36', fontWeight: 600, fontSize: 13, cursor: 'pointer', textDecoration: 'none' },
  connectResult: { marginTop: 10 },
  connectValue: { padding: '8px 10px', background: '#f6f8fa', borderRadius: 8, fontSize: 12, wordBreak: 'break-all', fontFamily: 'monospace' },
  connectError: { marginTop: 8, color: '#d93838', fontSize: 13 },
  checklist:    { listStyle: 'none', padding: 0, margin: '10px 0 0' },
  checkItem:    { fontSize: 13, lineHeight: 1.8 },
  wooBox:       { display: 'flex', alignItems: 'center', gap: 14, marginTop: 16, padding: '12px 14px', background: '#f6f8fb', borderRadius: 12, fontSize: 14, flexWrap: 'wrap' },
  // 8 כרטיסים - 4 בשורה (2 שורות מלאות, בלי כרטיס בודד שנשאר לבד), 2 במסך צר
  stats:        { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 8 },
  totalsLine:   { fontSize: 12.5, color: '#697386', marginBottom: 16, paddingInline: 4 },
  funnel:       { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, margin: '12px 0 8px' },
  sourceRow:    { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  rangeBar:     { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  rangeLabel:   { fontSize: 13, fontWeight: 700, color: '#4f566b' },
  segment:      { display: 'flex', flexWrap: 'wrap', border: '1.5px solid #e3e8ee', borderRadius: 10, overflow: 'hidden', background: '#fff' },
  segmentBtn:   { padding: '7px 14px', background: '#fff', color: '#697386', border: 'none', borderInlineStart: '1px solid #eef0f3', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  segmentBtnActive: { background: '#1f6feb', color: '#fff' },
  customRange:  { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  dateLabel:    { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4f566b', fontWeight: 600 },
  dateInput:    { padding: '6px 8px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 13, fontFamily: 'inherit' },
  rangeHint:    { fontSize: 12, color: '#8792a2' },
  stat:         { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12, padding: '12px 14px' },
  statValue:    { fontSize: 20, fontWeight: 800 },
  statLabel:    { fontSize: 12, color: '#697386', marginTop: 2 },
  twoCols:      { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 },
  sectionTitle: { margin: '0 0 12px', fontSize: 17, color: '#1a1f36' },
  staffRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f1f3f5', fontSize: 14 },
  linkBtn:      { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 14 },
  sub:          { color: '#8792a2', fontSize: 11.5, marginTop: 2 },
  muted:        { color: '#8792a2', fontSize: 13.5 },
  tableHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  filterBtn:    { padding: '6px 12px', background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#697386' },
  filterBtnActive: { background: '#eef4ff', color: '#1f6feb', borderColor: '#1f6feb' },
  tableWrap:    { overflowX: 'auto', border: '1px solid #e3e8ee', borderRadius: 12 },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:           { textAlign: 'right', padding: '10px 12px', background: '#f6f8fb', color: '#4f566b', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '1px solid #e3e8ee' },
  td:           { textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top', color: '#1a1f36' },
  strike:       { textDecoration: 'line-through', color: '#a3acb9' },
};

export default BusinessDetails;
